import { Link } from 'react-router-dom';
import { LegalShell } from './TermsPage';
import { usePaperTheme } from '@/hooks/usePaperTheme';

export function PrivacyPage() {
  usePaperTheme();
  return (
    <LegalShell title="Privacy Policy" updated="2026-04-23">
      <P>
        This policy explains what Clyra collects, why, and what your rights
        are. Short version: we collect the minimum needed to run the service
        and bill you correctly. We don't sell your data.
      </P>

      <H>What we collect</H>
      <ul className="ml-5 list-disc space-y-1 text-charcoal-muted">
        <li>
          <strong>Account data</strong> — email, bcrypt-hashed password (if you set one), plan,
          role, created/updated timestamps.
        </li>
        <li>
          <strong>Content</strong> — diagrams and prompts you submit, plus the
          model output we return to you.
        </li>
        <li>
          <strong>Usage</strong> — per-user daily generation counters for rate
          limiting (no prompt text retained outside your own diagrams).
        </li>
        <li>
          <strong>Technical</strong> — request IP, user-agent, approximate
          country (from IP or Cloudflare geo header), kept in logs for 30 days max.
        </li>
        <li>
          <strong>Payments</strong> — if you upgrade, we store a reference,
          status, amount, currency, and the payment provider's internal ID.
          We <em>do not</em> see your card number; Paystack or Polar handles that.
        </li>
      </ul>

      <H>Processors we use</H>
      <ul className="ml-5 list-disc space-y-1 text-charcoal-muted">
        <li><strong>Paystack</strong> — NGN payments (Nigerian customers).</li>
        <li><strong>Polar</strong> — Merchant of Record for non-NG customers; they handle checkout, tax, and invoicing globally.</li>
        <li><strong>OpenAI / Anthropic / Google</strong> — model inference. Prompts are sent to these providers; we do not enable training on your data. On the BYO plan your own key is used, so your traffic is governed by your provider agreement.</li>
        <li><strong>AWS Bedrock</strong> — optional managed inference for Pro on-platform.</li>
        <li><strong>Resend</strong> — transactional email (password resets, invite notifications).</li>
        <li><strong>Cloudflare</strong> — CDN + DDoS protection in front of the app.</li>
        <li><strong>Google (Sign in with Google)</strong> — if you choose this sign-in method, we receive your Google account's unique id, email address, and verified-email flag. We do not request calendar, contacts, drive, or any other Google API scopes.</li>
        <li><strong>GitHub (Sign in with GitHub)</strong> — if you choose this sign-in method, we receive your GitHub user id, primary verified email, and display name. We do not request <code>repo</code> or any write scope.</li>
      </ul>
      <P>
        For both Google and GitHub we only read the minimum profile fields
        needed to create or link your Clyra account. We never post on your
        behalf or access your content on those services.
      </P>

      <H>Why we collect it</H>
      <ul className="ml-5 list-disc space-y-1 text-charcoal-muted">
        <li>To run your account and keep your diagrams accessible.</li>
        <li>To enforce fair usage (rate limits, plan gates).</li>
        <li>To process payments and issue refunds.</li>
        <li>To prevent abuse (brief IP retention in logs).</li>
      </ul>
      <P>We do not use your content to train models. We do not run ad networks.</P>

      <H>Your rights</H>
      <P>
        You can export or delete your diagrams from the editor. You can delete
        your account at any time from Settings — this removes your profile and
        diagrams within 30 days. For GDPR / CCPA requests (access, portability,
        deletion), email us and we'll action within 30 days.
      </P>

      <H>Retention</H>
      <ul className="ml-5 list-disc space-y-1 text-charcoal-muted">
        <li>Account + diagrams: until you delete them.</li>
        <li>Request logs: 30 days.</li>
        <li>Payment records: 7 years (legal requirement for tax records).</li>
      </ul>

      <H>Cookies</H>
      <P>
        Two cookies — a signed session cookie (HttpOnly, Secure) and a CSRF
        double-submit cookie. No third-party tracking cookies. No analytics
        cookies without your consent.
      </P>

      <H>Transfers</H>
      <P>
        Data may be processed in the US, EU, and Nigeria depending on which
        processor is involved. We rely on Standard Contractual Clauses for
        EU → rest-of-world transfers.
      </P>

      <H>Changes</H>
      <P>
        We'll email you about material changes. The "last updated" date above
        reflects the current version.
      </P>

      <H>Contact</H>
      <P>
        Privacy questions / data requests:{' '}
        <Link to="#" onClick={(e) => { e.preventDefault(); window.location.href = 'mailto:oche@useclyra.com'; }} className="text-teal-600 hover:text-teal-500 transition-colors">
          oche@useclyra.com
        </Link>
      </P>

      <P className="mt-12 text-tiny text-charcoal-faint">
        ← <Link to="/" className="text-teal-600 hover:text-teal-500 transition-colors">Back to Clyra</Link>
      </P>
    </LegalShell>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 className="pt-6 text-base font-semibold text-charcoal">{children}</h2>;
}

function P({ children, className }: { children: React.ReactNode; className?: string }) {
  return <p className={className}>{children}</p>;
}
